import { useEffect, useState, useCallback, useRef } from "react";
import { API_BASE_URL } from "../utils/config";
import CustomSelect from "../components/CustomSelect";

const token = () => localStorage.getItem("accessToken");

async function apiFetch(url, opts = {}) {
  const res = await fetch(`${API_BASE_URL}${url}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(
      data.detail || data.non_field_errors?.[0] || JSON.stringify(data) || "Request failed"
    );
  }
  return res.status === 204 ? null : res.json();
}

function SearchableSelect({ options, value, onChange, placeholder = "Pilih..." }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));
  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm flex items-center justify-between text-left"
      >
        <span className={`truncate ${!selected ? "text-muted" : ""}`}>
          {selected ? selected.label : placeholder}
        </span>
        <svg className="w-4 h-4 shrink-0 ml-1 text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-white/50 dark:border-white/15 bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm shadow-lg flex flex-col max-h-56">
          <div className="p-2 border-b border-white/30 dark:border-white/10 shrink-0">
            <input
              autoFocus
              type="text"
              className="w-full rounded px-2 py-1 text-sm border border-white/50 dark:border-white/15 bg-white/50 dark:bg-slate-700/50"
              placeholder="Ketik untuk cari..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-muted">Tidak ditemukan</div>
            ) : (
              filtered.map((o) => (
                <div
                  key={o.value}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(String(o.value));
                    setOpen(false);
                    setQuery("");
                  }}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-brand-500/10 ${
                    String(o.value) === String(value) ? "bg-brand-500/15 font-medium" : ""
                  }`}
                >
                  {o.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const STATUS_LABEL = {
  PENDING: { label: "Menunggu", cls: "bg-yellow-400/20 text-yellow-700 dark:text-yellow-300" },
  APPROVED: { label: "Disetujui", cls: "bg-green-400/20 text-green-700 dark:text-green-300" },
  REJECTED: { label: "Ditolak", cls: "bg-red-400/20 text-red-600 dark:text-red-400" },
};

export default function PanelAssemblies() {
  const [assemblies, setAssemblies] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formComponents, setFormComponents] = useState([]);
  const [outputItemId, setOutputItemId] = useState("");
  const [outputQty, setOutputQty] = useState(1);
  const [locationId, setLocationId] = useState("");
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [compItemId, setCompItemId] = useState("");
  const [compQty, setCompQty] = useState(1);
  const [expandedId, setExpandedId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [asm, tmpls, its, locs] = await Promise.all([
        apiFetch("/api/panel-assemblies/"),
        apiFetch("/api/panel-templates/").catch(() => []),
        apiFetch("/api/items/?page_size=9999&status=active"),
        apiFetch("/api/locations/"),
      ]);
      setAssemblies(Array.isArray(asm) ? asm : []);
      setTemplates(Array.isArray(tmpls) ? tmpls : []);
      setItems((its?.results || its || []).filter((i) => i.is_active));
      setLocations(Array.isArray(locs) ? locs : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openForm = () => {
    setSelectedTemplate(null);
    setFormComponents([]);
    setOutputItemId("");
    setOutputQty(1);
    setLocationId("");
    setNote("");
    setFormError("");
    setCompItemId("");
    setCompQty(1);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setSelectedTemplate(null);
    setFormComponents([]);
  };

  // Stock available for an item AT a specific gudang — not the global total.
  // Approval validates per-location, so that's what the form must show too;
  // showing the global total can read as "stok aman" while the chosen
  // gudang actually has none of that component.
  const getLocationStock = (item, locId) => {
    if (!item) return 0;
    if (!locId) return item.quantity_available ?? 0;
    const ls = (item.location_stocks || []).find(
      (s) => String(s.location_id) === String(locId)
    );
    return ls?.available ?? 0;
  };

  const applyTemplate = (tmplId) => {
    const tmpl = templates.find((t) => t.id === Number(tmplId));
    if (!tmpl) {
      setSelectedTemplate(null);
      setFormComponents([]);
      setOutputItemId("");
      return;
    }
    setSelectedTemplate(tmpl);
    setOutputItemId(String(tmpl.output_item_id));
    setFormComponents(
      tmpl.components.map((c) => ({
        item_id: c.item_id,
        item_code: c.item_code,
        item_name: c.item_name,
        quantity: c.default_quantity,
      }))
    );
  };

  const addComponent = () => {
    if (!compItemId) return;
    const item = items.find((i) => i.id === Number(compItemId));
    if (!item) return;
    if (formComponents.some((c) => c.item_id === item.id)) {
      setFormError("Komponen ini sudah ada di daftar.");
      return;
    }
    setFormError("");
    setFormComponents((prev) => [
      ...prev,
      {
        item_id: item.id,
        item_code: item.code,
        item_name: item.name,
        quantity: compQty,
      },
    ]);
    setCompItemId("");
    setCompQty(1);
  };

  const removeComponent = (itemId) => {
    setFormComponents((prev) => prev.filter((c) => c.item_id !== itemId));
  };

  const updateCompQty = (itemId, qty) => {
    setFormComponents((prev) =>
      prev.map((c) => (c.item_id === itemId ? { ...c, quantity: Number(qty) } : c))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!outputItemId) return setFormError("Pilih item panel output.");
    if (!locationId) return setFormError("Pilih gudang sumber komponen.");
    if (formComponents.length === 0) return setFormError("Minimal 1 komponen.");
    setSaving(true);
    try {
      await apiFetch("/api/panel-assemblies/", {
        method: "POST",
        body: JSON.stringify({
          template_id: selectedTemplate?.id || null,
          output_item_id: Number(outputItemId),
          output_quantity: outputQty,
          location_id: Number(locationId),
          note,
          components: formComponents.map((c) => ({
            item_id: c.item_id,
            quantity: c.quantity,
          })),
        }),
      });
      closeForm();
      fetchAll();
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const outputItem = items.find((i) => i.id === Number(outputItemId));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Perakitan Panel</h1>
        <button
          onClick={openForm}
          className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700"
        >
          + Buat Rakitan
        </button>
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {loading && <p className="text-sm text-muted mb-3">Loading...</p>}

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="glass-card w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-semibold mb-4">Buat Rakitan Panel</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {templates.length > 0 && (
                <div>
                  <label className="block text-xs font-medium mb-1">
                    Gunakan Template (opsional)
                  </label>
                  <CustomSelect
                    value={selectedTemplate?.id ? String(selectedTemplate.id) : ""}
                    onChange={(v) => applyTemplate(v)}
                    placeholder="-- Tanpa template (manual) --"
                    options={templates.map((t) => ({ value: String(t.id), label: t.name }))}
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium mb-1">Panel Output (hasil rakitan)</label>
                  <SearchableSelect
                    value={outputItemId}
                    onChange={setOutputItemId}
                    placeholder="-- Pilih item panel --"
                    options={items.map((i) => ({ value: i.id, label: `${i.code} - ${i.name}` }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Jumlah Panel Dirakit</label>
                  <input
                    type="number"
                    min={1}
                    className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                    value={outputQty}
                    onChange={(e) => setOutputQty(Number(e.target.value))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Gudang Sumber</label>
                  <CustomSelect
                    value={locationId}
                    onChange={setLocationId}
                    placeholder="-- Pilih gudang --"
                    options={locations.map((l) => ({ value: String(l.id), label: l.name }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-2">Daftar Komponen</label>
                <div className="flex gap-2 mb-2">
                  <div className="flex-1">
                    <SearchableSelect
                      value={compItemId}
                      onChange={setCompItemId}
                      placeholder="-- Tambah komponen --"
                      options={items
                        .filter((i) => !formComponents.some((c) => c.item_id === i.id))
                        .map((i) => ({
                          value: i.id,
                          label: locationId
                            ? `${i.code} - ${i.name} (stok di gudang ini: ${getLocationStock(i, locationId)})`
                            : `${i.code} - ${i.name} (stok total: ${i.quantity_available ?? 0})`,
                        }))}
                    />
                  </div>
                  <input
                    type="number"
                    min={1}
                    className="w-20 rounded-md border border-white/50 dark:border-white/15 px-2 py-1.5 text-sm"
                    value={compQty}
                    onChange={(e) => setCompQty(Number(e.target.value))}
                  />
                  <button
                    type="button"
                    onClick={addComponent}
                    className="px-3 py-1.5 rounded-md bg-brand-600 text-white text-sm hover:bg-brand-700"
                  >
                    +
                  </button>
                </div>

                {formComponents.length > 0 && (() => {
                  const withStock = formComponents.map((c) => {
                    const item = items.find((i) => i.id === c.item_id);
                    return {
                      ...c,
                      quantity_available: getLocationStock(item, locationId),
                      minimum_stock: item?.minimum_stock ?? 0,
                    };
                  });
                  const emptyItems = withStock.filter((c) => c.minimum_stock > 0 && c.quantity_available === 0);
                  const lowItems = withStock.filter((c) => c.minimum_stock > 0 && c.quantity_available > 0 && c.quantity_available < c.minimum_stock);
                  return (
                    <>
                      {emptyItems.length > 0 && (
                        <div className="mb-2 flex items-start gap-2 rounded-lg bg-red-500/10 border border-red-400/30 px-3 py-2 text-xs text-red-700 dark:text-red-300">
                          <svg className="w-4 h-4 shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                          </svg>
                          <span>
                            <strong>Stok habis:</strong>{" "}
                            {emptyItems.map((c) => c.item_code).join(", ")}
                          </span>
                        </div>
                      )}
                      {lowItems.length > 0 && (
                        <div className="mb-2 flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-400/30 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
                          <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                          </svg>
                          <span>
                            <strong>Stok rendah:</strong>{" "}
                            {lowItems.map((c) => `${c.item_code} (${c.quantity_available}/${c.minimum_stock})`).join(", ")}
                          </span>
                        </div>
                      )}
                      <div className="glass-table-wrap overflow-x-auto">
                        <table className="glass-table text-xs">
                          <thead>
                            <tr>
                              <th className="text-left">Komponen</th>
                              <th className="text-right">Stok</th>
                              <th className="text-right">Qty × {outputQty} panel</th>
                              <th className="text-right">Total dibutuhkan</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {withStock.map((c) => {
                              const isEmpty = c.minimum_stock > 0 && c.quantity_available === 0;
                              const isLow = c.minimum_stock > 0 && c.quantity_available > 0 && c.quantity_available < c.minimum_stock;
                              return (
                                <tr key={c.item_id} className={isEmpty ? "bg-red-500/10" : isLow ? "bg-amber-500/10" : ""}>
                                  <td className="px-3 py-1.5">
                                    <div className="text-muted">{c.item_code}</div>
                                    <div className="font-medium">{c.item_name}</div>
                                  </td>
                                  <td className={`px-3 py-1.5 text-right font-medium ${isEmpty ? "text-red-600 dark:text-red-400" : isLow ? "text-amber-600 dark:text-amber-400" : ""}`}>
                                    {isEmpty && (
                                      <svg className="w-3 h-3 inline mr-0.5 mb-0.5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                                      </svg>
                                    )}
                                    {isLow && (
                                      <svg className="w-3 h-3 inline mr-0.5 mb-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                      </svg>
                                    )}
                                    {c.quantity_available}
                                  </td>
                                  <td className="px-3 py-1.5 text-right">
                                    <input
                                      type="number"
                                      min={1}
                                      className="w-16 rounded border border-white/50 dark:border-white/15 px-1 py-0.5 text-xs text-right"
                                      value={c.quantity}
                                      onChange={(e) => updateCompQty(c.item_id, e.target.value)}
                                    />
                                  </td>
                                  <td className="px-3 py-1.5 text-right font-medium">
                                    {c.quantity * outputQty}
                                  </td>
                                  <td className="px-3 py-1.5 text-right">
                                    <button
                                      type="button"
                                      onClick={() => removeComponent(c.item_id)}
                                      className="text-red-500 text-xs hover:text-red-700"
                                    >
                                      Hapus
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </>
                  );
                })()}
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Catatan</label>
                <textarea
                  className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Opsional"
                />
              </div>

              {formError && <p className="text-red-600 text-xs">{formError}</p>}

              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 rounded-lg border border-white/50 dark:border-white/15 text-sm"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50"
                >
                  {saving ? "Mengirim..." : "Submit Rakitan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assembly list */}
      {!loading && assemblies.length === 0 && (
        <p className="text-sm text-muted">Belum ada rakitan panel.</p>
      )}
      <div className="space-y-3">
        {assemblies.map((asm) => {
          const st = STATUS_LABEL[asm.status] || { label: asm.status, cls: "" };
          return (
            <div key={asm.id} className="glass-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-semibold text-sm">{asm.reference_code}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${st.cls}`}>
                      {st.label}
                    </span>
                  </div>
                  <p className="text-xs text-muted">
                    Output: <span className="font-medium">{asm.output_item_code} – {asm.output_item_name}</span>
                    {" "}&times; {asm.output_quantity}
                  </p>
                  <p className="text-xs text-muted">
                    Gudang: {asm.location_name || "-"} &bull; Dibuat: {new Date(asm.created_at).toLocaleString("id-ID")}
                  </p>
                  <p className="text-xs text-muted">
                    Dirakit oleh:{" "}
                    <span className="font-semibold text-strong">{asm.user || "-"}</span>
                  </p>
                  {asm.template_name && (
                    <p className="text-xs text-muted">Template: {asm.template_name}</p>
                  )}
                  {asm.note && <p className="text-xs text-muted mt-0.5">Catatan: {asm.note}</p>}
                  {asm.status !== "PENDING" && asm.approved_by && (
                    <p className="text-xs text-muted">
                      {asm.status === "APPROVED" ? "Disetujui" : "Ditolak"} oleh {asm.approved_by}
                      {asm.approved_at && ` · ${new Date(asm.approved_at).toLocaleString("id-ID")}`}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setExpandedId(expandedId === asm.id ? null : asm.id)}
                  className="shrink-0 text-xs px-2 py-1 rounded border border-white/50 dark:border-white/15 hover:bg-white/20"
                >
                  {expandedId === asm.id ? "Tutup" : `${asm.components.length} komponen`}
                </button>
              </div>

              {expandedId === asm.id && (
                <div className="mt-3 glass-table-wrap overflow-x-auto">
                  <table className="glass-table text-xs">
                    <thead>
                      <tr>
                        <th className="text-left">Kode</th>
                        <th className="text-left">Komponen</th>
                        <th className="text-right">Qty/panel</th>
                        <th className="text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {asm.components.map((c) => (
                        <tr key={c.id}>
                          <td className="px-3 py-1.5">{c.item_code}</td>
                          <td className="px-3 py-1.5">{c.item_name}</td>
                          <td className="px-3 py-1.5 text-right">{c.quantity}</td>
                          <td className="px-3 py-1.5 text-right font-medium">
                            {c.quantity * asm.output_quantity}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
